// src/components/paginas/Productos.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import MostrarProducto from '../ui/mostrarProducto';

/* ===== helpers consistentes con el proyecto ===== */
const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE = resolveApiBase();

const fetchJSON = async (url, opts={}) => {
  const resp = await fetch(url, { credentials: 'include', cache: 'no-store', ...opts });
  let j = null; try { j = await resp.json(); } catch {}
  if (!resp.ok) {
    const msg = (j && (j.msg || j.message)) || resp.statusText || `HTTP ${resp.status}`;
    const err = new Error(msg); err.status = resp.status; err.payload = j; throw err;
  }
  return j;
};
/* =============================================== */

const ITEMS_PER_PAGE = 9;

export default function Productos() {
  const [productos, setProductos] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);

  const abortRef = useRef(null);

  const fetchProductos = useCallback(async () => {
    try {
      abortRef.current?.abort?.();
      const controller = new AbortController();
      abortRef.current = controller;

      setCargando(true); setError('');
      const data = await fetchJSON(`${API_BASE}/api/productos?t=${Date.now()}`, { signal: controller.signal });

      // normalizamos por si en algún momento el backend envolviera los datos
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data) ? data.data
        : Array.isArray(data?.productos) ? data.productos
        : [];

      setProductos(list);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error al obtener productos:', err);
      setError(String(err.message || err));
      setProductos([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { fetchProductos(); }, [fetchProductos]);

  // refrescar al volver a la pestaña
  useEffect(() => {
    const onFocus = () => fetchProductos();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchProductos]);

  // debounce búsqueda
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(busqueda.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [busqueda]);

  // filtrar
  const productosFiltrados = useMemo(() => {
    const base = Array.isArray(productos) ? productos : [];
    if (!debouncedQ) return base;
    return base.filter(p => {
      const nombre = String(p?.nombre || '').toLowerCase();
      const marca  = String(p?.marca || '').toLowerCase();
      const pres   = String(p?.presentacion || '').toLowerCase();
      return nombre.includes(debouncedQ) || marca.includes(debouncedQ) || pres.includes(debouncedQ);
    });
  }, [productos, debouncedQ]);

  // paginación
  useEffect(() => { setPaginaActual(1); }, [debouncedQ]);
  const totalPaginas = Math.max(1, Math.ceil(productosFiltrados.length / ITEMS_PER_PAGE));
  const inicio = (paginaActual - 1) * ITEMS_PER_PAGE;
  const productosPagina = productosFiltrados.slice(inicio, inicio + ITEMS_PER_PAGE);

  const Paginacion = () => {
    if (totalPaginas <= 1) return null;
    const btn = (i, label=i) => (
      <button
        key={i}
        onClick={() => setPaginaActual(i)}
        disabled={paginaActual === i}
        className={`px-3 py-1 rounded-lg border text-sm
          ${paginaActual === i ? 'bg-sky-600 text-white border-sky-600'
                               : 'bg-white hover:bg-slate-50 border-slate-300 text-slate-700'}`}
      >
        {label}
      </button>
    );
    const nodes = [];
    if (totalPaginas <= 7) {
      for (let i=1;i<=totalPaginas;i++) nodes.push(btn(i));
    } else {
      const push = (x)=>nodes.push(x);
      push(btn(1));
      let d = Math.max(2, paginaActual-2);
      let h = Math.min(totalPaginas-1, paginaActual+2);
      if (paginaActual <= 4) { d = 2; h = 5; }
      if (paginaActual >= totalPaginas - 3) { d = totalPaginas-4; h = totalPaginas-1; }
      if (d > 2) push(<span key="se" className="px-2">…</span>);
      for (let i=d;i<=h;i++) push(btn(i));
      if (h < totalPaginas-1) push(<span key="ee" className="px-2">…</span>);
      push(btn(totalPaginas));
    }
    return (
      <div className="flex justify-center items-center gap-1 mt-6">
        <button
          onClick={() => setPaginaActual(1)}
          disabled={paginaActual===1}
          className="px-3 py-1 rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-50"
        >«</button>
        <button
          onClick={() => setPaginaActual(p => Math.max(1, p-1))}
          disabled={paginaActual===1}
          className="px-3 py-1 rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-50"
        >‹</button>
        {nodes}
        <button
          onClick={() => setPaginaActual(p => Math.min(totalPaginas, p+1))}
          disabled={paginaActual===totalPaginas}
          className="px-3 py-1 rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-50"
        >›</button>
        <button
          onClick={() => setPaginaActual(totalPaginas)}
          disabled={paginaActual===totalPaginas}
          className="px-3 py-1 rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-50"
        >»</button>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-3xl font-bold">Productos</h1>
          <p className="text-sm text-slate-500">Explora y gestiona tu catálogo.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar por nombre, marca o presentación…"
              className="pl-10 pr-3 py-2 rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-sky-300 outline-none w-72"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔎</span>
          </div>
          <button
            onClick={fetchProductos}
            className="px-4 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-50"
            title="Refrescar"
          >
            ↻
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-4 p-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-700">
          {error}
        </div>
      )}

      {/* grid/cards */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
        {cargando ? (
          Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm animate-pulse">
              <div className="aspect-[4/3] rounded-xl bg-slate-200" />
              <div className="mt-3 h-4 w-2/3 bg-slate-200 rounded" />
              <div className="mt-2 h-3 w-1/2 bg-slate-200 rounded" />
              <div className="mt-2 h-3 w-1/3 bg-slate-200 rounded" />
            </div>
          ))
        ) : productosPagina.length > 0 ? (
          productosPagina.map((producto) => (
            <MostrarProducto key={producto.id} producto={producto} />
          ))
        ) : (
          <div className="col-span-full text-center text-slate-500 py-12">
            No se encontraron productos.
          </div>
        )}
      </div>

      <Paginacion />
    </div>
  );
}
